import AggregateError from "es-aggregate-error";

import type { AppMetadata } from "../appBridge/AppMetadata";
import type { ErrorHandling, FatalErrors } from "../Logging/ErrorHandling";
import type { Logger } from "../Logging/Log";
import { APIError } from "../utils/errors";
import type { SessionEntityType } from "./models/session";

type HttpPayload = Record<string, unknown>[] | Record<string, unknown> | string | boolean | number | undefined;

type callApiParams<TBody extends HttpPayload> = {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  body?: TBody;
  query?: Record<string, string>;
  headers?: Record<string, string>;
  debugMsg?: string;
};

export const APICallsHistory: string[] = [];
const defaultAPIEndpoint = "https://monster-customizer.classdojo.com";
export class API {
  static Logger: typeof Logger;
  static ErrorHandling: typeof ErrorHandling;
  static FatalErrors: typeof FatalErrors;

  static fetchWrapper: (category: string, url: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

  static host = typeof window !== "undefined" ? window.API_ENDPOINT ?? defaultAPIEndpoint : defaultAPIEndpoint;
  private static forwardedHost?: string;
  static appMetadata?: AppMetadata;

  static init(
    host: string,
    entityType: SessionEntityType | "gameServer",
    opts: {
      Logger: typeof Logger;
      ErrorHandling: typeof ErrorHandling;
      FatalErrors: typeof FatalErrors;
      fetchWrapper: (category: string, url: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
    }
  ) {
    API.host = host;
    API.Logger = opts.Logger;
    API.ErrorHandling = opts.ErrorHandling;
    API.FatalErrors = opts.FatalErrors;
    API.fetchWrapper = opts.fetchWrapper;
    API.forwardedHost =
      entityType === "gameServer"
        ? undefined
        : entityType === "teacher"
        ? "teach.classdojo.com"
        : "student.classdojo.com";
  }

  static call<TResponse extends HttpPayload>(path: string): Promise<TResponse>;
  static call<TResponse extends HttpPayload, TBody extends HttpPayload = undefined>(
    params: callApiParams<TBody>
  ): Promise<TResponse>;
  // eslint-disable-next-line sonarjs/cognitive-complexity
  static async call<TResponse extends HttpPayload, TBody extends HttpPayload = undefined>(
    params: callApiParams<TBody> | string
  ): Promise<TResponse> {
    const { method, path, body = undefined, headers = {}, debugMsg = "" } = this.getParams(params);
    let url = path;
    if (path[0] === "/") {
      url = `${API.host}${path}`;
    }

    const allHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      ...headers,
      ...API.platformHeaders(API.appMetadata),
    };

    if (this.forwardedHost) {
      // set header so ClassDojo API can correctly identify user session
      allHeaders["x-forwarded-host"] = this.forwardedHost;
    }

    let strBody: string | undefined;
    if (body) {
      strBody = JSON.stringify(body);
    }

    try {
      const response = await API.fetchWrapper("api", url, {
        method,
        body: strBody,
        credentials: "include",
        headers: allHeaders,
      });

      if (!response.ok) {
        const responseText = await response.text();
        throw new APIError(`Error in callApi: ${response.status}`, method, response, responseText);
      }

      // keep a log of most recent api calls for error debugging purposes
      APICallsHistory.unshift(`[${new Date().toISOString()}] ${method} ${url} :: ${debugMsg}`);
      APICallsHistory.length = Math.min(APICallsHistory.length, 10);

      // HTTP 204 No Content makes fetch throw when calling `response.json()`
      // because there is no content in the response, so just return undefined in that case
      return response.status !== 204 ? response.json() : undefined;
    } catch (err: unknown) {
      if (err instanceof APIError) {
        const { response } = err;
        if (response.status === 401 || response.status === 403) {
          API.ErrorHandling.instance?.triggerFatalError(API.FatalErrors.UserLoggedOut);
        }
        const urlWithoutIds = err.response.url?.replace(/\/([a-f\d]{24})/gi, "/:id");
        API.Logger.logWarn("API Error", {
          method: err.method,
          url: urlWithoutIds,
          status: response.status,
          statusText: response.statusText,
        });
      }

      let allHeadersSanitized = headers;
      if (typeof allHeadersSanitized === "object" && "Authorization" in allHeadersSanitized) {
        allHeadersSanitized = { ...allHeadersSanitized, Authorization: "~redacted~" };
      }
      throw new AggregateError(
        [err],
        `Error in callApi::fetch\nurl: ${url}\nmethod: ${method}\ndebug msg: ${debugMsg}\nbody: ${
          body && JSON.stringify(body)
        }\nheaders: ${JSON.stringify(allHeadersSanitized)}\napi calls: ${APICallsHistory.join("\n")}`
      );
    }
  }

  private static getParams<TBody extends HttpPayload = undefined>(
    params: callApiParams<TBody> | string
  ): callApiParams<TBody> {
    if (typeof params === "string") {
      const method = "GET";
      const path = params;

      return { method, path };
    }

    return params;
  }

  // A platform specific User-Agent header value based on AppMetadata. This value is passed with each request to api to ensure that
  // platform-specific feature switch values are calculated properly. Currently the User-Agent is only
  // returned for iOS. Android and Web rely on other request headers for version information.
  // eg. ClassDojo/8.2.0-1159 (iPhone; iOS 14.5; Scale/2.00)
  private static getUserAgent(metadata: AppMetadata): string | undefined {
    if (metadata.platform && metadata.platform.toLowerCase() !== "ios") {
      return undefined;
    }

    return `ClassDojo/${metadata.appVersion}-${metadata.buildNumber} (${metadata.device}; iOS ${metadata.osVersion}; Scale/2.00)`;
  }

  private static getClientIdentifier(appMetadata: AppMetadata) {
    if (appMetadata.platform?.toLowerCase() === "web") return "Web";
    if (appMetadata.platform?.toLowerCase() === "android") return "com.classdojo.android";
    if (appMetadata.platform?.toLowerCase() === "android_web") return "StudentPWA";
    return "iOS";
  }

  private static getClientVersion(appMetadata: AppMetadata) {
    let version = appMetadata.appVersion;
    const useBuildNumber = appMetadata.platform?.toLowerCase() === "ios";
    if (useBuildNumber && appMetadata.appVersion && appMetadata.buildNumber) version += `-${appMetadata.buildNumber}`;
    return version || "0.0.0";
  }

  private static platformHeaders(appMetadata: AppMetadata | undefined) {
    if (!appMetadata) {
      return {};
    }

    const platformHeaders: Record<string, string> = {
      "X-Client-Identifier": this.getClientIdentifier(appMetadata),
      "X-Client-Version": this.getClientVersion(appMetadata),
    };
    const userAgent = this.getUserAgent(appMetadata);
    if (userAgent !== undefined) {
      platformHeaders["User-Agent"] = userAgent;
    }
    return platformHeaders;
  }
}
